// MediaPipe Pose Landmarker wrapper.
//
// Lazy-loads the WASM runtime and pose model, then exposes a single
// detect(videoFrame, timestampMs) method that returns landmarks and a
// category mask we use for frontal-area pixel counting.

import {
  FilesetResolver,
  PoseLandmarker,
  PoseLandmarkerResult,
} from '@mediapipe/tasks-vision';

// Hosted CDN URLs — MediaPipe ships the WASM runtime and model weights as
// static files. Using the Google-hosted CDN is fine for dev; in production
// we'd mirror these into our own CDN/S3 bucket.
const WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task';

export interface Landmark {
  x: number; // 0..1 normalized to image width
  y: number; // 0..1 normalized to image height
  z: number;
  visibility: number;
}

/** 3D landmark in real-world metres. Origin at the hip midpoint. */
export interface WorldLandmark {
  x: number; // metres, right of hip
  y: number; // metres, above hip
  z: number; // metres, in front of hip (depth)
  visibility: number;
}

export interface PoseDetectionResult {
  landmarks: Landmark[] | null;
  worldLandmarks: WorldLandmark[] | null;
  segmentationMask: ImageData | null; // binary person mask, same resolution as input
}

export class PoseDetector {
  private landmarker: PoseLandmarker | null = null;
  private initializing: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.landmarker) return;
    if (this.initializing) return this.initializing;

    this.initializing = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
      this.landmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: 'GPU', // falls back to CPU/WASM if WebGL unavailable
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputSegmentationMasks: true,
      });
    })();

    await this.initializing;
  }

  /**
   * Run pose + segmentation on a single video frame.
   *
   * @param video the HTMLVideoElement (or canvas) to analyze
   * @param timestampMs monotonically increasing timestamp for tracking
   */
  detect(
    video: HTMLVideoElement,
    timestampMs: number
  ): PoseDetectionResult | null {
    if (!this.landmarker) return null;

    const result: PoseLandmarkerResult = this.landmarker.detectForVideo(
      video,
      timestampMs
    );

    const landmarks: Landmark[] | null =
      result.landmarks && result.landmarks.length > 0
        ? result.landmarks[0].map((lm) => ({
            x: lm.x,
            y: lm.y,
            z: lm.z,
            visibility: lm.visibility ?? 0,
          }))
        : null;

    const worldLandmarks: WorldLandmark[] | null =
      result.worldLandmarks && result.worldLandmarks.length > 0
        ? result.worldLandmarks[0].map((lm) => ({
            x: lm.x,
            y: lm.y,
            z: lm.z,
            visibility: lm.visibility ?? 0,
          }))
        : null;

    let segmentationMask: ImageData | null = null;
    if (result.segmentationMasks && result.segmentationMasks.length > 0) {
      const mask = result.segmentationMasks[0];
      // MediaPipe returns an MPMask; we convert to ImageData for easy pixel iteration.
      segmentationMask = mpMaskToImageData(mask);
      // Release the underlying WebGL/WASM texture to avoid leaks.
      mask.close();
    }

    return { landmarks, worldLandmarks, segmentationMask };
  }

  close(): void {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }
}

// MediaPipe's MPMask may be backed by WebGL, a Float32Array, or a Uint8Array
// depending on the delegate. We normalize to Uint8ClampedArray ImageData
// where a pixel is "person" if its channel value > 128.
function mpMaskToImageData(mask: any): ImageData {
  const width = mask.width;
  const height = mask.height;

  // Prefer the Float32Array accessor (returns values 0..1 for person probability)
  let floatArr: Float32Array | null = null;
  try {
    floatArr = mask.getAsFloat32Array();
  } catch {
    floatArr = null;
  }

  const rgba = new Uint8ClampedArray(width * height * 4);

  if (floatArr) {
    for (let i = 0; i < floatArr.length; i++) {
      const v = floatArr[i] > 0.5 ? 255 : 0;
      rgba[i * 4] = v;
      rgba[i * 4 + 1] = v;
      rgba[i * 4 + 2] = v;
      rgba[i * 4 + 3] = 255;
    }
  } else {
    // Fallback: treat everything as non-person
    for (let i = 0; i < width * height; i++) {
      rgba[i * 4 + 3] = 255;
    }
  }

  return new ImageData(rgba, width, height);
}
